import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { useCheckInSend, useCheckInContext } from "../context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { useKimAddress } from "../../../../../lib/use-kim-address";
import { KioskStep } from "../../../../../components/kiosk-step";
import {
  KioskLoadingPanel,
  KioskStatePanel,
} from "../../../../../components/kiosk-state-panel";

/**
 * Quarterly Coverage Check Step
 *
 * Automatically checks whether the GKV patient has valid coverage proof for
 * the current billing quarter, then routes to the appropriate next step. One
 * loader with plain wording (KC-35); a failed check continues to the end of
 * the check-in, which is already committed, with the reception clarifying
 * the insurance (no dead end without "Zurück").
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <QuarterlyCoverageCheckStep />
 * ```
 */
export function QuarterlyCoverageCheckStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const context = useCheckInContext();
  const [status, setStatus] = useState<"loading" | "error">("loading");

  // Check if KIM address is available (override or from bridge)
  const { kimInfo, isLoading: isLoadingKim } = useKimAddress();

  // Play audio on mount
  useAudio("checkIn-quarterlyCoverageCheck");

  useEffect(
    function syncStatus() {
      // Wait for KIM address check to complete
      if (isLoadingKim) return;
      let active = true;

      const checkCoverage = async () => {
        if (!context.patientId) {
          if (active) setStatus("error");
          return;
        }

        try {
          const result =
            await docCirrusRpcClient.frontdesk.checkQuarterlyCoverage({
              patientId: context.patientId,
            });
          if (!active) return;

          // Unproven this quarter + no KIM account: every eEB delivery path
          // (KIM / QR / phone) ultimately delivers to the practice's KIM address,
          // so without one the patient cannot self-serve the Versicherungsnachweis
          // at the kiosk. Complete check-in without eEB (the practice verifies
          // coverage offline) — without faking coverage as the old code did.
          if (!result.hasCurrentQuarterCoverage && !kimInfo) {
            send({ type: "SKIP_EEB" });
            return;
          }

          // Route from server-owned coverage facts. The known-patient eEB path
          // never asks the patient to confirm an IK or choose an insurer.
          send({
            type: "QUARTERLY_CHECK_RESULT",
            hasCurrentQuarterCoverage: result.hasCurrentQuarterCoverage,
            coverageQuarter: result.coverageQuarter,
            hasPreviousCoverage: result.hasPreviousCoverage,
            previousInsurerIknr: result.previousInsurerIknr,
            previousInsurerName: result.previousInsurerName,
            lastCardReadDate: result.lastCardReadDate,
          });
        } catch (error) {
          if (!active) return;
          frontdeskDiagnosticLogger.error(
            "Quarterly coverage check failed",
            error,
          );
          setStatus("error");
        }
      };

      void checkCoverage();
      return function stopQuarterlyCoverageUpdate() {
        active = false;
      };
    },
    [context.patientId, send, kimInfo, isLoadingKim],
  );

  if (status === "error") {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t("self-service.check-in.quarterly-check.title-v2")}
        primaryAction={{
          label: t("common.next"),
          icon: ArrowRight,
          onClick: () => send({ type: "SKIP_EEB" }),
          testId: "frontdesk-quarterly-check-continue-btn",
        }}
      >
        <KioskStatePanel
          tone="warning"
          title={t("self-service.check-in.quarterly-check.error")}
          body={t("self-service.check-in.quarterly-check.error-body")}
          testId="frontdesk-quarterly-check-error"
        />
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.quarterly-check.title-v2")}
    >
      <KioskLoadingPanel
        title={t("self-service.check-in.quarterly-check.checking-v2")}
        testId="quarterly-check-loading"
      />
    </KioskStep>
  );
}
